import { c as _c } from "react/compiler-runtime";
import { Fragment } from 'react';
import { mergeClasses } from 'minimal-shared/utils';
import { m, useSpring, useTransform } from 'framer-motion';
import Box from '@mui/material/Box';
import Portal from '@mui/material/Portal';
import { styled, useTheme } from '@mui/material/styles';
import { createClasses } from 'src/theme/create-classes';

// ----------------------------------------------------------------------

export const scrollProgressClasses = {
  circular: createClasses('scroll__progress__circular'),
  linear: createClasses('scroll__progress__linear')
};
export function ScrollProgress(t0) {
  const $ = _c(42);
  let className;
  let other;
  let portal;
  let progressProps;
  let size;
  let slotProps;
  let sx;
  let t1;
  let t2;
  let t3;
  let variant;
  if ($[0] !== t0) {
    ({
      size,
      portal,
      variant,
      slotProps,
      className,
      thickness: t1,
      whenScroll: t2,
      color: t3,
      progress: progressProps,
      sx,
      ...other
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = other;
    $[3] = portal;
    $[4] = progressProps;
    $[5] = size;
    $[6] = slotProps;
    $[7] = sx;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = variant;
  } else {
    className = $[1];
    other = $[2];
    portal = $[3];
    progressProps = $[4];
    size = $[5];
    slotProps = $[6];
    sx = $[7];
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    variant = $[11];
  }
  const thickness = t1 === undefined ? 3.6 : t1;
  const whenScroll = t2 === undefined ? "y" : t2;
  const color = t3 === undefined ? "primary" : t3;
  const theme = useTheme();
  const isRtl = theme.direction === "rtl";
  let t4;
  let t5;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [0, -1];
    t5 = [0, 1];
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  const transformProgress = useTransform(progressProps, t4, t5);
  const progress = isRtl && whenScroll === "x" ? transformProgress : progressProps;
  let t6;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      stiffness: 100,
      damping: 30,
      restDelta: 0.001
    };
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const scaleX = useSpring(progress, t6);
  const progressSize = variant === "circular" ? size ?? 64 : size ?? 3;
  let t7;
  if ($[15] !== className || $[16] !== color || $[17] !== other || $[18] !== progress || $[19] !== progressSize || $[20] !== sx || $[21] !== theme.vars || $[22] !== thickness) {
    t7 = () => <CircularRoot xmlns="http://w3.org/2000/svg" viewBox={`0 0 ${progressSize} ${progressSize}`} className={mergeClasses([scrollProgressClasses.circular, className])} sx={[{
      width: progressSize,
      height: progressSize,
      ...(color !== "inherit" && {
        color: theme.vars.palette[color].main
      })
    }, ...(Array.isArray(sx) ? sx : [sx])]} {...other}><circle cx={progressSize / 2} cy={progressSize / 2} r={progressSize / 2 - thickness - 4} strokeWidth={thickness} strokeOpacity={0.2} /><m.circle cx={progressSize / 2} cy={progressSize / 2} r={progressSize / 2 - thickness - 4} strokeWidth={thickness} style={{
        pathLength: progress
      }} /></CircularRoot>;
    $[15] = className;
    $[16] = color;
    $[17] = other;
    $[18] = progress;
    $[19] = progressSize;
    $[20] = sx;
    $[21] = theme.vars;
    $[22] = thickness;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const renderCircular = t7;
  let t8;
  if ($[24] !== className || $[25] !== color || $[26] !== other || $[27] !== progressSize || $[28] !== scaleX || $[29] !== sx || $[30] !== theme.vars) {
    t8 = () => <LinearRoot className={mergeClasses([scrollProgressClasses.linear, className])} sx={[{
      height: progressSize,
      ...(color !== "inherit" && {
        background: `linear-gradient(135deg, ${theme.vars.palette[color].light}, ${theme.vars.palette[color].main})`
      })
    }, ...(Array.isArray(sx) ? sx : [sx])]} style={{
      scaleX
    }} {...other} />;
    $[24] = className;
    $[25] = color;
    $[26] = other;
    $[27] = progressSize;
    $[28] = scaleX;
    $[29] = sx;
    $[30] = theme.vars;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const renderLinear = t8;
  const PortalWrapper = portal ? Portal : Fragment;
  const t9 = slotProps?.wrapper;
  let t10;
  if ($[32] !== renderCircular || $[33] !== renderLinear || $[34] !== variant) {
    t10 = variant === "circular" ? renderCircular() : renderLinear();
    $[32] = renderCircular;
    $[33] = renderLinear;
    $[34] = variant;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== t10 || $[37] !== t9) {
    t11 = <Box {...t9}>{t10}</Box>;
    $[36] = t10;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  let t12;
  if ($[39] !== PortalWrapper || $[40] !== t11) {
    t12 = <PortalWrapper>{t11}</PortalWrapper>;
    $[39] = PortalWrapper;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
}
const CircularRoot = styled(m.svg)(({
  theme
}) => ({
  transform: 'rotate(-90deg)',
  color: theme.vars.palette.text.primary,
  circle: {
    fill: 'none',
    strokeDashoffset: 0,
    stroke: 'currentColor'
  }
}));
const LinearRoot = styled(m.div)(({
  theme
}) => ({
  top: 0,
  left: 0,
  right: 0,
  transformOrigin: '0%',
  backgroundColor: theme.vars.palette.text.primary
}));